import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import API_CONFIG from '../../config/api';

interface FAQ {
  _id: string;
  question: string;
  answer: string;
  category: 'general' | 'test-request' | 'test-results' | 'payment' | 'technical';
  active: boolean;
  order: number;
  isOpen: boolean;
  createdAt: string;
  updatedAt: string;
}

const FAQ: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: '',
    category: 'general' as 'general' | 'test-request' | 'test-results' | 'payment' | 'technical',
    active: true,
    order: 0,
    isOpen: false
  });

  // Fetch FAQs from API
  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch(API_CONFIG.getUrl('faqs'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('خطا در دریافت سوالات متداول');
      }

      const data = await response.json();
      setFaqs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در دریافت سوالات متداول');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (editingFaq) {
      // Update the FAQ being edited
      setFaqs(faqs.map(faq => 
        faq._id === editingFaq 
          ? { 
              ...faq, 
              [name]: name === 'active' || name === 'isOpen' ? value === 'true' : 
                      name === 'order' ? parseInt(value) || 0 : value 
            }
          : faq
      ));
    } else {
      // Update the new FAQ
      setNewFaq({
        ...newFaq,
        [name]: name === 'active' || name === 'isOpen' ? value === 'true' : 
                name === 'order' ? parseInt(value) || 0 : value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      const token = localStorage.getItem('authToken');
      const currentFaq = editingFaq 
        ? faqs.find(faq => faq._id === editingFaq)
        : newFaq;

      if (!currentFaq) return;

      if (currentFaq.question.trim() === '' || currentFaq.answer.trim() === '') {
        alert('لطفاً سوال و جواب را وارد کنید.');
        return;
      }

      const url = editingFaq ? API_CONFIG.getUrl(`faqs/${editingFaq}`) : API_CONFIG.getUrl('faqs');
      const method = editingFaq ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(currentFaq)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'خطا در ذخیره سوال متداول');
      }

      // Refresh FAQs list
      await fetchFaqs();
      
      // Reset form
      setEditingFaq(null);
      setShowFaqForm(false);
      setNewFaq({
        question: '',
        answer: '',
        category: 'general',
        active: true,
        order: 0,
        isOpen: false
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در ذخیره سوال متداول');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const startEditing = (id: string) => {
    setEditingFaq(id);
    setShowFaqForm(true);
  };

  const cancelEditing = () => {
    setEditingFaq(null);
    setShowFaqForm(false);
    setNewFaq({
      question: '',
      answer: '',
      category: 'general',
      active: true,
      order: 0,
      isOpen: false
    });
    // Refresh to get original data
    fetchFaqs();
  };

  const toggleFaqStatus = async (id: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(API_CONFIG.getUrl(`faqs/${id}/toggle`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('خطا در تغییر وضعیت سوال متداول');
      }

      await fetchFaqs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در تغییر وضعیت سوال متداول');
    }
  };

  const deleteFaq = async (id: string) => {
    if (!window.confirm('آیا از حذف این سوال متداول اطمینان دارید؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(API_CONFIG.getUrl(`faqs/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('خطا در حذف سوال متداول');
      }

      await fetchFaqs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در حذف سوال متداول');
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'general': return 'عمومی';
      case 'test-request': return 'درخواست آزمایش';
      case 'test-results': return 'نتایج آزمایش';
      case 'payment': return 'پرداخت';
      case 'technical': return 'فنی';
      default: return category;
    }
  };

  const currentFaq = editingFaq 
    ? faqs.find(faq => faq._id === editingFaq)
    : newFaq;

  if (loading) {
    return (
     
        <div className="p-4">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg">در حال بارگذاری...</div>
          </div>
        </div>
  
    );
  }

  return (
   
      <div className="p-4">
        <div className="card bg-white rounded-sm p-4 border border-slate-300">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-slate-800">مدیریت سوالات متداول</h1>
            <button 
              onClick={() => {
                setEditingFaq(null);
                setShowFaqForm(!showFaqForm);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {showFaqForm && !editingFaq ? 'انصراف' : 'افزودن سوال متداول جدید'}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {showFaqForm && (
            <div className="mb-8 bg-gray-50 p-6 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">
                {editingFaq ? 'ویرایش سوال متداول' : 'افزودن سوال متداول جدید'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">سوال</label>
                    <input
                      type="text"
                      name="question"
                      value={currentFaq?.question || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">دسته‌بندی</label>
                    <select
                      name="category"
                      value={currentFaq?.category || 'general'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="general">عمومی</option>
                      <option value="test-request">درخواست آزمایش</option>
                      <option value="test-results">نتایج آزمایش</option>
                      <option value="payment">پرداخت</option>
                      <option value="technical">فنی</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ترتیب نمایش</label>
                    <input
                      type="number"
                      name="order"
                      value={currentFaq?.order || 0}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                    <select
                      name="active"
                      value={currentFaq?.active ? 'true' : 'false'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="true">فعال</option>
                      <option value="false">غیرفعال</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">باز بودن پیش‌فرض</label>
                    <select
                      name="isOpen"
                      value={currentFaq?.isOpen ? 'true' : 'false'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="true">باز</option>
                      <option value="false">بسته</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">جواب</label>
                  <textarea
                    name="answer"
                    value={currentFaq?.answer || ''}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="جواب سوال متداول را وارد کنید..."
                  />
                </div>
                <div className="mt-6 flex justify-end space-x-2">
                  {editingFaq && (
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="ml-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      انصراف
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting
                      ? 'در حال ارسال...'
                      : (editingFaq ? 'به‌روزرسانی سوال متداول' : 'افزودن سوال متداول')}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">شناسه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">سوال</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">دسته‌بندی</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ترتیب</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {faqs.map(faq => (
                  <tr key={faq._id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{faq._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm max-w-xs truncate" title={faq.question}>
                      {faq.question}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {getCategoryLabel(faq.category)}
                    </td>
                    <td className="py-3 px-4 text-sm">{faq.order}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        faq.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {faq.active ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => toggleFaqStatus(faq._id)}
                          className={`ml-2 px-3 py-1 rounded text-xs ${
                            faq.active 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {faq.active ? 'غیرفعال کردن' : 'فعال کردن'}
                        </button>
                        <button 
                          onClick={() => startEditing(faq._id)}
                          className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                        >
                          ویرایش
                        </button>
                        <button 
                          onClick={() => deleteFaq(faq._id)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    
  );
};

export default FAQ;
